import { Cell, Grid } from 'bold-ui'
import { Box } from 'components/Box'
import { Form, FormRenderProps } from 'components/form'
import { SexoEnum, TipoAtendimentoProfissional } from 'graphql/types.generated'
import { useMemo, useState } from 'react'
import { metaPath } from 'util/metaPath'
import { AtendimentoProfissional } from 'view/atendimentos/types/AtendimentoProfissionalModel'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { AtestadoCadastroFormRouter } from './AtestadoCadastroFormRouter'
import { AtestadoSelectField } from './components/AtestadoSelectField'
import AtestadoModel, {
  AtestadoModeloEnum,
  AtestadoSelectModel,
  atestadoSelectModelDefaultRecord,
  CidadaoAtestado,
} from './components/model-atestado'

export interface AtestadoCadastroProps {
  atendimentoId: ID
  prontuarioId: ID
  cidadao: CidadaoAtendimento
  atendimentoProfissional: AtendimentoProfissional
  handleSubmit: (values: AtestadoModel) => void
}

interface AtestadoCadastroModel {
  atestadoModelo: AtestadoSelectModel | null
}

const atestadoModeloPadrao = atestadoSelectModelDefaultRecord[AtestadoModeloEnum.PADRAO]
const path = metaPath<AtestadoCadastroModel>()

export function AtestadoCadastroView(props: Readonly<AtestadoCadastroProps>) {
  const { atendimentoId, prontuarioId, cidadao, atendimentoProfissional, handleSubmit } = props
  const [needRefetch, setNeedRefetch] = useState(false)

  const accessLicenca = useMemo(() => canAccessLicencaMaternidade(cidadao, atendimentoProfissional), [
    atendimentoProfissional,
    cidadao,
  ])

  const cidadaoAtestado = useMemo(() => convertCidadaoAtendimentoToCidadaoAtestado(cidadao), [cidadao])

  const renderForm = (formProps: FormRenderProps<AtestadoCadastroModel>) => {
    const atestadoModelo = formProps.values.atestadoModelo

    const handleUpdateDelete = (modelo: AtestadoSelectModel) => {
      formProps.form.change(path.atestadoModelo.absolutePath(), modelo)
      setNeedRefetch(true)
    }

    const handleUpdateModelo = (nomeModelo: string) => {
      if (!atestadoModelo) return

      handleUpdateDelete({
        ...atestadoModelo,
        nome: nomeModelo,
      })
    }

    return (
      <Box>
        <Grid>
          <Cell size={4}>
            <AtestadoSelectField
              required
              label='Modelo'
              name={path.atestadoModelo}
              canAccessLicencaMaternidade={accessLicenca}
              needRefetch={needRefetch}
              onRefetch={() => setNeedRefetch(false)}
            />
          </Cell>

          <AtestadoCadastroFormRouter
            atendimentoId={atendimentoId}
            prontuarioId={prontuarioId}
            atestadoModelo={atestadoModelo}
            cidadao={cidadaoAtestado}
            dataAtendimento={atendimentoProfissional.iniciadoEm}
            onDeleteModelo={() => handleUpdateDelete(atestadoModeloPadrao)}
            onUpdateModelo={handleUpdateModelo}
            onSubmit={handleSubmit}
          />
        </Grid>
      </Box>
    )
  }

  return <Form<AtestadoCadastroModel> initialValues={{ atestadoModelo: atestadoModeloPadrao }} render={renderForm} />
}

const convertCidadaoAtendimentoToCidadaoAtestado = (cidadao: CidadaoAtendimento): CidadaoAtestado => {
  return {
    id: cidadao.id,
    nome: cidadao.nome,
    nomeSocial: cidadao.nomeSocial,
    cpf: cidadao.cpf,
    cns: cidadao.cns,
  }
}

const canAccessLicencaMaternidade = (
  cidadao: CidadaoAtendimento,
  atendimentoProfissional: AtendimentoProfissional
): boolean => {
  if (cidadao?.idadeEmAnos < 5) return false
  if (cidadao?.identidadeGeneroDbEnum === null && cidadao?.sexo !== SexoEnum.FEMININO) return false

  return atendimentoProfissional?.tipo !== TipoAtendimentoProfissional.ATENDIMENTO_ODONTOLOGICO
}
